import type { OfferMode, UserIntentSegment } from "@/lib/paywall/user-intent";
import type { PackageType } from "@/lib/revenuecat/config";
import type {
  NormalizedRevenueCatPurchaseError,
  RevenueCatPurchaseErrorCategory,
} from "@/lib/revenuecat/purchase-errors";

export const CHECKOUT_TELEMETRY_VERSION = 2;

export type CheckoutSurface = "paywall_page" | "pricing_section";

export type CheckoutStage =
  | "package_validation"
  | "attributes"
  | "sdk_purchase"
  | "entitlement_verification";

export type CheckoutAttempt = {
  id: string;
  packageType: PackageType;
  offerMode: OfferMode;
  userIntentSegment: UserIntentSegment;
  surface: CheckoutSurface;
  analyticsProperties?: Record<string, unknown>;
  startedAt: number;
  hasBeenPending: boolean;
  sdkSettled: boolean;
  terminalErrorCategory: RevenueCatPurchaseErrorCategory | null;
};

export type CheckoutTelemetryEvent =
  | "subscription_checkout_started"
  | "subscription_checkout_cancelled"
  | "subscription_checkout_pending"
  | "subscription_purchase_failed"
  | "subscription_checkout_verification_completed"
  | "subscription_checkout_client_succeeded";

export type PostHogCaptureClient = {
  capture: (eventName: string, properties?: Record<string, unknown>) => unknown;
};

export function createCheckoutAttempt(
  input: Omit<
    CheckoutAttempt,
    "id" | "startedAt" | "hasBeenPending" | "sdkSettled" | "terminalErrorCategory"
  >
): CheckoutAttempt {
  return {
    ...input,
    id:
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`,
    startedAt: Date.now(),
    hasBeenPending: false,
    sdkSettled: false,
    terminalErrorCategory: null,
  };
}

export function getCheckoutElapsedMs(attempt: CheckoutAttempt) {
  return Math.max(0, Date.now() - attempt.startedAt);
}

export function captureCheckoutTelemetry(
  posthog: PostHogCaptureClient | null | undefined,
  eventName: CheckoutTelemetryEvent,
  attempt: CheckoutAttempt,
  stage: CheckoutStage,
  properties?: {
    error?: NormalizedRevenueCatPurchaseError;
    verificationResult?: "active" | "inactive" | "unknown";
    verificationTrigger?: "automatic_pending" | "manual" | "terminal_error";
  }
) {
  if (!posthog || !process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

  const error = properties?.error;
  posthog.capture(eventName, {
    ...attempt.analyticsProperties,
    checkout_attempt_id: attempt.id,
    checkout_telemetry_version: CHECKOUT_TELEMETRY_VERSION,
    stage,
    package: attempt.packageType,
    offer_mode: attempt.offerMode,
    user_intent_segment: attempt.userIntentSegment,
    surface: attempt.surface,
    elapsed_ms: getCheckoutElapsedMs(attempt),
    previously_pending: attempt.hasBeenPending,
    ...(error
      ? {
          error_category: error.category,
          error_code: error.errorCode,
          provider_status_code: error.statusCode,
          provider_backend_error_code: error.backendErrorCode,
        }
      : {}),
    ...(properties?.verificationResult
      ? {
          verification_result: properties.verificationResult,
          verification_trigger: properties.verificationTrigger,
        }
      : {}),
  });
}
